import ReactGA from 'react-ga4';

// Type definition for window.plausible
declare global {
  interface Window {
    plausible?: (event: string, options?: { props?: Record<string, any> }) => void;
    // Use any[] instead of a more specific type to avoid conflicts
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Initialize Google Analytics
 * @param measurementId Your Google Analytics measurement ID (ex: G-XXXXXXXXXX)
 */
export const initGA = (measurementId: string): void => {
  try {
    // Only initialize in production or if explicitly enabled in development
    if (import.meta.env.PROD || import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
      ReactGA.initialize(measurementId, {
        testMode: !import.meta.env.PROD,
        gaOptions: {
          cookieFlags: 'SameSite=None;Secure'
        }
      });
      // console.log('Google Analytics initialized with ID:', measurementId);
    } else {
      // console.log('Google Analytics not initialized in development mode');
    }
  } catch (error) {
    //  console.error('Error initializing Google Analytics:', error);
  }
};

/**
 * Track a page view
 * @param path The current page path
 * @param title The page title
 */
export const pageView = (path: string, title?: string): void => {
  try {
    if (!path) {
      return;
    }
    
    // Track with Google Analytics
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });
    
    // Track with Plausible
    if (window.plausible) {
      window.plausible('pageview');
    }
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

/**
 * Track a custom event
 * @param category Event category
 * @param action String that identifies the event
 * @param label Event label (optional)
 * @param value Event value (optional)
 * @param props Additional properties for Plausible (optional)
 */
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number,
  props?: Record<string, any>
): void => {
  try {
    // Track with Google Analytics
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
    
    // Track with Plausible
    if (window.plausible) {
      // Format event name for Plausible (typically category:action)
      const plausibleEvent = `${category}:${action}`;
      window.plausible(plausibleEvent, { props });
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Set user ID for tracking
 * @param userId The user ID to set
 */
export const setUserId = (userId: string): void => {
  try {
    if (userId) {
      ReactGA.set({ userId });
      // Note: Plausible doesn't support user ID tracking by default
      // as it's privacy-focused, but you can pass it as a custom prop if needed
    }
  } catch (error) {
    console.error('Error setting user ID:', error);
  }
}; 